import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

export const postApi = {
  list: (workspaceId: string, params?: { brandId?: string; platform?: string; status?: string }) =>
    api.get<Post[]>(`/api/workspaces/${workspaceId}/posts`, { params }).then((r) => r.data),
  /**
   * Paginated variant for the all-posts grid (Polish step 1). Reads
   * `X-Total-Count` for the page footer. Backend caps `size` at 200.
   */
  listPaged: (
    workspaceId: string,
    params: {
      brandId?: string;
      platform?: string;
      status?: string;
      /** V148 — IG media format filter (FEED / REEL / CAROUSEL / STORY).
       *  Sent as `mediaFormat` query param; backend applies an in-memory
       *  filter on the response. */
      mediaFormat?: string;
      /** Sort by real post date: 'oldest' = ascending; omit / 'newest' = newest-first. */
      sort?: string;
      page: number;
      size: number;
    },
  ) =>
    api.get<Post[]>(`/api/workspaces/${workspaceId}/posts`, { params }).then((r) => ({
      items: r.data,
      total: parseInt(r.headers['x-total-count'] ?? `${r.data.length}`, 10),
    })),
  create: (workspaceId: string, data: CreatePostRequest) =>
    api.post<Post>(`/api/workspaces/${workspaceId}/posts`, data).then((r) => r.data),
  createManual: (workspaceId: string, data: ManualPostRequest) =>
    api.post<Post[]>(`/api/workspaces/${workspaceId}/posts/manual`, data).then((r) => r.data),
  update: (workspaceId: string, postId: string, data: UpdatePostRequest) =>
    api.patch<Post>(`/api/workspaces/${workspaceId}/posts/${postId}`, data).then((r) => r.data),
  delete: (workspaceId: string, postId: string) =>
    api.delete(`/api/workspaces/${workspaceId}/posts/${postId}`).then((r) => r.data),
  // Bulk-delete by id — backs the review page "Delete all". Owner-only
  // server-side. Returns `{ deleted }` — the count that actually landed
  // (already-gone / cross-workspace ids are skipped, not counted).
  bulkDelete: (workspaceId: string, postIds: string[]) =>
    api
      .post<{
        deleted: number;
      }>(`/api/workspaces/${workspaceId}/posts/bulk-delete`, { postIds })
      .then((r) => r.data),
  retry: (workspaceId: string, postId: string) =>
    api.post<Post>(`/api/workspaces/${workspaceId}/posts/${postId}/retry`).then((r) => r.data),
  // V123 — retry the reel-generation pipeline for posts whose
  // videoGenerationStatus is 'failed'. Different from `retry` which
  // handles publish-side failures (post.status === 'FAILED').
  retryReel: (workspaceId: string, postId: string) =>
    api.post<Post>(`/api/workspaces/${workspaceId}/posts/${postId}/retry-reel`).then((r) => r.data),
  generate: (workspaceId: string, data: GeneratePostsRequest) =>
    api
      .post<GeneratePostsResponse>(`/api/workspaces/${workspaceId}/posts/generate`, data)
      .then((r) => r.data),
  generateCarousel: (workspaceId: string, postId: string) =>
    api
      .post<Post>(`/api/workspaces/${workspaceId}/posts/${postId}/generate-carousel`, {})
      .then((r) => r.data),
  stats: (workspaceId: string) =>
    api.get<DashboardStats>(`/api/workspaces/${workspaceId}/posts/stats`).then((r) => r.data),
  /** Per-post lifecycle audit log — feeds the trace modal. */
  events: (workspaceId: string, postId: string) =>
    api
      .get<PostEvent[]>(`/api/workspaces/${workspaceId}/posts/${postId}/events`)
      .then((r) => r.data),
  /** V147 — per-post generation trace (single row, latest only).
   *  Returns 404 when no trace exists (e.g. legacy post pre-V147). */
  generationTrace: (workspaceId: string, postId: string) =>
    api
      .get<GenerationTraceResponse>(
        `/api/workspaces/${workspaceId}/posts/${postId}/generation-trace`,
      )
      .then((r) => r.data),
  /** Phase 7f Step 1 — re-run citability scoring after a manual edit.
   *  Plan-gated, owner-only, 5/hour per post. */
  rescoreCitability: (workspaceId: string, postId: string) =>
    api
      .post<GeoCitabilityResult>(`/api/workspaces/${workspaceId}/posts/${postId}/geo-score`)
      .then((r) => r.data),
  /** Re-run AI generation with the existing GEO citability suggestions
   *  injected as constraints. Replaces post.copy + hashtags, clears
   *  the score (frontend shows scoring-loading state), kicks off an
   *  async rescore. Plan-gated, owner-only, shares 5/hour cap with
   *  rescoreCitability. Refuses on PUBLISHED posts. */
  improveGeo: (workspaceId: string, postId: string) =>
    api
      .post<Post>(`/api/workspaces/${workspaceId}/posts/${postId}/improve-geo`)
      .then((r) => r.data),
  /** Tier 1.1 — bulk CSV preview. Multipart upload of a CSV; returns
   *  per-row validation result without writing anything. */
  bulkImportPreview: (workspaceId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post<BulkImportPreviewResponse>(`/api/workspaces/${workspaceId}/posts/bulk-import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  /** Tier 1.1 + V138 — bulk CSV confirm. Takes the validated row list
   *  back from preview, kicks off a background `import_jobs` worker,
   *  and returns the pending job row immediately. Frontend polls
   *  `getBulkImportJob` until status terminates; the structured
   *  result lives in `job.result` (JSON-encoded BulkImportResultResponse). */
  bulkImportConfirm: (workspaceId: string, posts: BulkImportRowInput[]) =>
    api
      .post<ImportJob>(`/api/workspaces/${workspaceId}/posts/bulk-import/confirm`, { posts })
      .then((r) => r.data),
  getBulkImportJob: (workspaceId: string, jobId: string) =>
    api
      .get<ImportJob>(`/api/workspaces/${workspaceId}/posts/bulk-import/jobs/${jobId}`)
      .then((r) => r.data),
  listBulkImportJobs: (workspaceId: string) =>
    api
      .get<ImportJob[]>(`/api/workspaces/${workspaceId}/posts/bulk-import/jobs`)
      .then((r) => r.data),
};

// ── Bulk import types (Tier 1.1) ───────────────────────────────────

export interface BulkImportPreviewResponse {
  validPosts: BulkImportRow[];
  invalidPosts: BulkImportError[];
  totalRows: number;
  validCount: number;
  invalidCount: number;
}

export interface BulkImportRow {
  rowNumber: number;
  platform: string;
  content: string;
  scheduledAt: string | null;
  brandId: string;
  brandName: string | null;
  hashtags: string[];
  characterCount: number;
  warnings: string[];
}

export interface BulkImportError {
  rowNumber: number;
  field: string;
  message: string;
  rawContent: string | null;
}

export interface BulkImportRowInput {
  platform: string;
  content: string;
  scheduledAt: string | null;
  brandId: string;
  hashtags: string[];
}

export interface BulkImportResultResponse {
  created: number;
  failed: number;
  errors: BulkImportError[];
}

export interface GeoCitabilityResult {
  total: number;
  breakdown: {
    brandClarity: number;
    factualDensity: number;
    keywordRelevance: number;
    answerPotential: number;
    entityReinforcement: number;
  };
  suggestions: string[];
  topKeywords: string[];
}

/** One row in the post lifecycle audit log (V45). */
export interface PostEvent {
  id: string;
  postId: string;
  eventType: string;
  detail: string | null;
  /** V85 — JSON blob for decision events. Shape: { inputs: Record, outputs: Record, reason: string }.
   *  Null for plain lifecycle events. */
  metadata: string | null;
  actor: string;
  createdAt: string | null;
}

/** V147 — per-post generation trace. Returned by `postApi.generationTrace`
 *  and `adminApi.getGenerationTrace`. `steps` is the JSONB array parsed
 *  on the backend — each step's input/output/metadata is open JSON so
 *  the Generation tab can render arbitrary shapes via a JSON viewer. */
export interface GenerationTraceStep {
  stepKey: string;
  label: string;
  startedAt: string | null;
  durationMs: number;
  input: unknown;
  output: unknown;
  metadata: unknown;
  /** 'ok' | 'warn' | 'regenerated' | 'error' */
  status: string;
}

export interface GenerationTraceResponse {
  id: string;
  postId: string | null;
  brandId: string;
  brandName: string | null;
  workspaceId: string;
  platform: string;
  createdAt: string | null;
  totalDurationMs: number;
  totalCostEur: number;
  status: string;
  errorMessage: string | null;
  steps: GenerationTraceStep[];
}

/** V85 — connection-scoped audit log row. Shape parallels PostEvent so
 *  one xyflow component renders both. */
export interface ConnectionEvent {
  id: string;
  connectionId: string;
  workspaceId: string;
  eventType: string;
  detail: string | null;
  metadata: string | null;
  actor: string;
  createdAt: string | null;
}
